import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

function CompareReports({ initialCompare, onConsumeInitial }) {
  const [ticker, setTicker] = useState('');
  const [searchedTicker, setSearchedTicker] = useState('');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReports, setSelectedReports] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [initialHandled, setInitialHandled] = useState(false);

  const fetchReportsForTicker = useCallback(async (tickerOverride) => {
    const searchTicker = tickerOverride || ticker;
    if (!searchTicker.trim()) {
      setError('Please enter a ticker symbol');
      return;
    }

    setLoading(true);
    setError(null);
    setReports([]);
    if (!tickerOverride) {
      setSelectedReports([]);
      setComparisonResult(null);
    }

    try {
      const response = await axios.get(`/api/compare/reports-for-ticker?ticker=${searchTicker.trim().toUpperCase()}`);
      if (response.data.success) {
        setReports(response.data.data);
        setSearchedTicker(response.data.ticker);
        if (response.data.data.length < 2) {
          setError(`Only ${response.data.data.length} report(s) found for ${searchTicker.toUpperCase()}. Need at least 2 reports to compare.`);
        }
        return response.data.data;
      } else {
        setError(response.data.error || 'Failed to fetch reports');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
    return [];
  }, [ticker]);

  React.useEffect(() => {
    if (initialCompare && !initialHandled) {
      setInitialHandled(true);
      const { reportId1, reportId2, ticker: initTicker } = initialCompare;
      if (initTicker) {
        setTicker(initTicker);
      }
      setSelectedReports([reportId1, reportId2]);

      const runAutoCompare = async () => {
        setComparing(true);
        setError(null);
        try {
          if (initTicker) {
            await fetchReportsForTicker(initTicker);
          }
          const response = await axios.post('/api/compare/analyze', {
            report_id_1: reportId1,
            report_id_2: reportId2,
          });
          if (response.data.success) {
            setComparisonResult(response.data.data);
          } else {
            setError(response.data.error || 'Failed to compare reports');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to compare reports');
        } finally {
          setComparing(false);
        }
      };

      runAutoCompare();
      if (onConsumeInitial) onConsumeInitial();
    }
  }, [initialCompare, initialHandled, fetchReportsForTicker, onConsumeInitial]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReportsForTicker();
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports((prev) => {
      if (prev.includes(reportId)) {
        return prev.filter((id) => id !== reportId);
      }
      if (prev.length >= 2) {
        return [prev[1], reportId];
      }
      return [...prev, reportId];
    });
  };

  const handleCompare = async () => {
    if (selectedReports.length !== 2) {
      setError('Please select exactly 2 reports to compare');
      return;
    }

    setComparing(true);
    setError(null);
    setComparisonResult(null);

    try {
      const response = await axios.post('/api/compare/analyze', {
        report_id_1: selectedReports[0],
        report_id_2: selectedReports[1],
      });

      if (response.data.success) {
        setComparisonResult(response.data.data);
      } else {
        setError(response.data.error || 'Failed to compare reports');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to compare reports');
    } finally {
      setComparing(false);
    }
  };

  const getDecisionChip = (decision) => {
    if (!decision) return <Chip label="N/A" size="small" />;
    const colorMap = {
      BUY: { color: 'success' },
      SELL: { color: 'error' },
      HOLD: { color: 'warning' },
      OUTPERFORM: { sx: { bgcolor: '#81c784', color: '#1b5e20' } },
      UNDERPERFORM: { sx: { bgcolor: '#ef9a9a', color: '#b71c1c' } },
    };
    const config = colorMap[decision] || { color: 'default' };
    return (
      <Chip
        label={decision}
        color={config.color}
        size="small"
        sx={{ fontWeight: 'bold', ...config.sx }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDecisionChangeIndicator = () => {
    if (!comparisonResult) return null;
    const older = comparisonResult.older_report.final_decision;
    const newer = comparisonResult.newer_report.final_decision;
    
    if (older === newer) {
      return (
        <Chip 
          label={`Maintained: ${older || 'N/A'}`} 
          color="info" 
          size="small" 
        />
      );
    }
    
    const ratingOrder = { BUY: 1, OUTPERFORM: 2, HOLD: 3, UNDERPERFORM: 4, SELL: 5 };
    const olderScore = ratingOrder[older] || 3;
    const newerScore = ratingOrder[newer] || 3;
    const isImprovement = newerScore < olderScore;
    
    return (
      <Chip
        icon={isImprovement ? <TrendingUpIcon /> : <TrendingDownIcon />}
        label={`${older || 'N/A'} → ${newer || 'N/A'}`}
        color={isImprovement ? 'success' : 'error'}
        size="small"
      />
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" color="primary">
          Compare Analysis Reports
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
          Enter a ticker symbol to find all available reports, then select two reports to compare how the analysis has changed over time.
        </Typography>
        
        <Box component="form" onSubmit={handleSearch} display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Enter ticker (e.g., AAPL)"
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 250,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !ticker.trim()}
          >
            {loading ? <CircularProgress size={20} /> : 'Find Reports'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {reports.length > 0 && !comparisonResult && (
        <Paper sx={{ mb: 3, backgroundColor: 'background.paper' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" color="secondary">
                Reports for {searchedTicker} ({reports.length} found)
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CompareArrowsIcon />}
                onClick={handleCompare}
                disabled={selectedReports.length !== 2 || comparing}
              >
                {comparing ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Analyzing...
                  </>
                ) : (
                  `Compare Selected (${selectedReports.length}/2)`
                )}
              </Button>
            </Box>
            <Typography variant="caption" color="textSecondary">
              Select exactly 2 reports to compare
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ color: 'primary.main' }}></TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Analysis Date</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Created At</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Decision</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow
                    key={report.id}
                    hover
                    onClick={() => handleSelectReport(report.id)}
                    selected={selectedReports.includes(report.id)}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 255, 0, 0.1)',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 255, 0, 0.15)',
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedReports.includes(report.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{report.analysis_date || '-'}</TableCell>
                    <TableCell>{formatDate(report.created_at)}</TableCell>
                    <TableCell>{getDecisionChip(report.final_decision)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.note || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {comparisonResult && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" color="primary">
              Comparison Results: {comparisonResult.ticker}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setComparisonResult(null);
                setSelectedReports([]);
              }}
            >
              New Comparison
            </Button>
          </Box>

          <Box display="flex" gap={2} mb={3}>
            <Card sx={{ flex: 1, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="overline" color="textSecondary">Older Report</Typography>
                <Typography variant="h6" color="secondary">{comparisonResult.older_report.analysis_date}</Typography>
                <Box mt={1}>
                  {getDecisionChip(comparisonResult.older_report.final_decision)}
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  Created: {formatDate(comparisonResult.older_report.created_at)}
                </Typography>
              </CardContent>
            </Card>

            <Box display="flex" alignItems="center">
              <CompareArrowsIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>

            <Card sx={{ flex: 1, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="overline" color="textSecondary">Newer Report</Typography>
                <Typography variant="h6" color="secondary">{comparisonResult.newer_report.analysis_date}</Typography>
                <Box mt={1}>
                  {getDecisionChip(comparisonResult.newer_report.final_decision)}
                </Box>
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  Created: {formatDate(comparisonResult.newer_report.created_at)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
              Decision Change:
            </Typography>
            {getDecisionChangeIndicator()}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              AI-Powered Comparison Analysis
            </Typography>
            <Box sx={{ 
              '& h1, & h2, & h3, & h4, & h5': { color: '#00bfff', mt: 2, mb: 1 },
              '& p': { color: '#e0e0e0', mb: 1.5 },
              '& ul, & ol': { color: '#e0e0e0', pl: 2 },
              '& li': { mb: 0.5 },
              '& strong': { color: '#00ff00' },
            }}>
              <ReactMarkdown>{comparisonResult.comparison_analysis}</ReactMarkdown>
            </Box>
          </Paper>
        </Box>
      )}

      {!loading && reports.length === 0 && searchedTicker && (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.paper' }}>
          <Typography color="textSecondary">
            No completed reports found for {searchedTicker}. Run some analyses first.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default CompareReports;
